import { useState, useMemo, type PropsWithChildren } from 'react';
import { styled, css } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Popover } from 'radix-ui';
import { links } from '../../utils/consts.ts';
import { type TranslationKey } from '../../utils/i18n.ts';
import { useUpMedia } from '../../hooks';
import { type ColorName } from '../../styles/colors.ts';
import { resetListStyles } from '../../styles/helpers.ts';
import { DesktopButton as DefDesktopButton, MobileButton as DefMobileButton } from '../buttons';
import { Discord } from '../icons';
import { DesktopText, MobileText } from '../texts';

export type HelpMenuProps = { asideOffsets?: number } & PropsWithChildren;

const HelpMenu = ({ asideOffsets = 0, children }: HelpMenuProps) => {
  const { t } = useTranslation();

  const [open, setOpen] = useState(false);

  const desktop = useUpMedia('md');

  const Title = useMemo(() => (desktop ? DesktopTitle : MobileTitle), [desktop]);

  const Button = useMemo(() => (desktop ? DesktopButton : MobileButton), [desktop]);

  const handleClick = (type: ButtonType) => {
    switch (type) {
      case 'discord':
        window.open(links.discord, '_blank', 'noreferrer');
        break;
      default:
        throw new Error(`No such help button type ${type}`);
    }
    setOpen(false);
  };

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>{children}</Popover.Trigger>
      <Popover.Portal>
        <Content
          sideOffset={2}
          collisionPadding={{
            left: asideOffsets,
            right: asideOffsets,
          }}
        >
          <ContentHeader>
            <Title>{t('HELP_POPUP__title')}</Title>
          </ContentHeader>
          <ContentButtonsList>
            {buttons.map(({ type, label, props }) => (
              <ContentButtonsListItem key={type}>
                {/* @ts-expect-error Button type mismatch */}
                <Button {...props} onClick={() => handleClick(type)}>
                  {type === 'discord' && <DiscordIcon />}
                  {t(label)}
                </Button>
              </ContentButtonsListItem>
            ))}
          </ContentButtonsList>
        </Content>
      </Popover.Portal>
    </Popover.Root>
  );
};

// noinspection CssUnresolvedCustomProperty
const Content = styled(Popover.Content)`
  width: var(--radix-popper-available-width);
  padding: 15px 20px 28px;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.getColor('shark')};
  background-color: ${({ theme }) => theme.getColor('mirage')};

  ${({ theme }) => theme.getUpMedia('md')} {
    width: auto;
    min-width: ${({ theme }) => theme.responsive(355)};
    padding: ${({ theme }) => theme.spacing(15, 12, 28)};
    border-radius: ${({ theme }) => theme.responsive(12)};
  }
`;

const commonDesktopOffsets = css`
  padding-left: ${({ theme }) => theme.responsive(18)};
  padding-right: ${({ theme }) => theme.responsive(18)};
`;

const ContentHeader = styled.header`
  display: flex;
  flex-direction: column;
  padding-top: ${({ theme }) => theme.responsive(24)};
  padding-bottom: ${({ theme }) => theme.responsive(18)};
  ${commonDesktopOffsets};
  border-bottom: 1px solid ${({ theme }) => theme.getColor('shark')};
`;

const DesktopTitle = styled(DesktopText).attrs({
  variant: 'h3',
  asTag: 'h2',
})`
  color: ${({ theme }) => theme.getColor('completelyWhite')};
`;

const MobileTitle = styled(MobileText).attrs({
  variant: 'h3',
})`
  color: ${({ theme }) => theme.getColor('completelyWhite')};
`;

const ContentButtonsList = styled.ul`
  ${resetListStyles};
  display: flex;
  flex-direction: column;
  margin-top: ${({ theme }) => theme.responsive(8)};
  ${commonDesktopOffsets};
`;

const ContentButtonsListItem = styled.li`
  & + & {
    margin-top: 10px;

    ${({ theme }) => theme.getUpMedia('md')} {
      margin-top: ${({ theme }) => theme.responsive(10)};
    }
  }
`;

const commonButtonsStyles = css<CommonButtonsStyles>`
  width: 100%;
  text-align: unset;
  span {
    text-transform: unset;
    display: flex;
    justify-content: center;
    align-items: center;
    letter-spacing: -0.4px;
  }
`;

type CommonButtonsStyles = {
  $active: boolean;
};

const MobileButton = styled(DefMobileButton).attrs({
  size: 'normal',
})<CommonButtonsStyles>`
  ${commonButtonsStyles};
  font-size: 15px;
`;

const DesktopButton = styled(DefDesktopButton)<CommonButtonsStyles>`
  ${commonButtonsStyles};
`;

const DiscordIcon = styled(Discord)`
  margin-right: ${({ theme }) => theme.responsive(8)};
  ${({ theme }) => theme.getUpMedia('md')} {
    position: relative;
    top: ${({ theme }) => theme.responsive(1)};
  }
`;

type ButtonType = 'discord';

const buttons: ReadonlyArray<{
  type: ButtonType;
  label: TranslationKey;
  props?: {
    color?: ColorName;
  };
}> = [
  {
    type: 'discord',
    label: 'MODES_POPUP__discordButton',
    props: {
      color: 'cornflowerBlue',
    },
  },
];

export { HelpMenu };
